import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CircleHelp, Info, LogOut, Settings, User } from "lucide-react";

import mayamindLogo from "@/assets/MayaMindLogo.svg";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import LogoutDialog from "@/components/custom/LogoutDialog";
import { authSession } from "@/core/api/authSession";
import { useGetMeQuery } from "@/features/profile/infrastructure/api/ProfileApi";
import { textStyles } from "@/lib/textStyles";

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const MobileTopBar = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: profile } = useGetMeQuery();
  const [openLogout, setOpenLogout] = useState(false);

  const displayName = profile?.name ?? "";
  const initials = displayName ? getInitials(displayName) : "";

  return (
    <>
      <header className="fixed inset-x-0 top-0 z-40 flex h-14 items-center justify-between border-b border-gray-200 bg-white/95 px-4 backdrop-blur lg:hidden">
        <button
          type="button"
          onClick={() => navigate("/patients")}
          className="flex items-center"
        >
          <img
            src={mayamindLogo}
            alt="Maya-mind"
            className="h-7 w-auto"
          />
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button type="button" className="rounded-full">
              <Avatar className="h-8 w-8">
                <AvatarFallback style={{ ...textStyles.body, fontWeight: 700 }}>
                  {initials || "MM"}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={() => navigate("/profile")}>
              <User className="h-4 w-4" />
              {t("profileMenu.profile")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/settings")}>
              <Settings className="h-4 w-4" />
              {t("profileMenu.settings")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/faq")}>
              <CircleHelp className="h-4 w-4" />
              {t("profileMenu.faq")}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/about")}>
              <Info className="h-4 w-4" />
              {t("profileMenu.about")}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setOpenLogout(true)}
              className="text-[#C62828] focus:text-[#C62828]"
            >
              <LogOut className="h-4 w-4" />
              {t("profileMenu.logout")}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      <LogoutDialog
        open={openLogout}
        onClose={() => setOpenLogout(false)}
        onConfirm={() => {
          setOpenLogout(false);
          authSession.clearTokens();
          navigate("/login", { replace: true });
        }}
      />
    </>
  );
};

export default MobileTopBar;

